import { ChangeDetectorRef, Injectable, Signal, WritableSignal, computed, effect, inject, signal, untracked } from '@angular/core';
import { AvailableAttributes } from 'src/app/interfaces/available-attributes';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { Design } from 'src/app/interfaces/design';
import { Form } from 'src/app/interfaces/form';
import { Material } from 'src/app/interfaces/material';
import { /*findAlternateConflicts,*/ formatFingerSizeForB2BSKU } from 'src/app/utils/configured-item.util';
import { toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observer, Subject, map, pairwise } from 'rxjs';
import { getURLKeyFromItemKey } from 'src/app/utils/url';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { Size } from 'src/app/interfaces/size';
import { EngravingType } from 'src/app/interfaces/engraving-type';
import { Width } from 'src/app/interfaces/width';
import { CheckoutService } from './checkout.service';
import { assert, assertTrue } from 'src/app/utils/assert.util';
import { RulesService } from 'src/app/services/rules/rules.service';
import { SizesService } from 'src/app/services/sizes/sizes.service';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { HistoryItem } from 'src/app/interfaces/history-item';
import { Navigation2Service } from './navigation2.service';
import { MaterialsService } from 'src/app/services/materials/materials.service';
import { WidthsService } from 'src/app/services/widths/widths.service';
import { FormsService } from 'src/app/services/forms/forms.service';
import { DesignsService } from 'src/app/services/designs/designs.service';
import { SkuMapService } from 'src/app/services/sku-map/sku-map.service';
import { B2BInput } from 'src/app/interfaces/b2b-input';
import { PricingObject } from 'src/app/interfaces/pricing-object';
import { Attribute } from 'src/app/interfaces/attribute';
import { SystemChange } from 'src/app/interfaces/system-change';
import { StoneArrangementsWithSettingsService } from 'src/app/services/stone-arrangementsWithSettings/stone-arrangements-with-settings.service';
import { StoneTypesWithShapesService } from 'src/app/services/stone-types-with-shapes/stone-types-with-shapes.service';
import { StoneSizesService } from 'src/app/services/stone-sizes/stone-sizes.service';
import { StoneArrangementsService } from 'src/app/services/stone-arrangements/stone-arrangements.service';
import { StoneSettingsService } from 'src/app/services/stone-settings/stone-settings.service';
import { StoneTypesService } from 'src/app/services/stone-types/stone-types.service';
import { StoneShapesService } from 'src/app/services/stone-shapes/stone-shapes.service';
import { GemstoneTypeWithShape } from 'src/app/interfaces/gemstone-type-with-shape';
import { GemstoneArrangementWithSetting } from 'src/app/interfaces/gemstone-arrangement-with-setting';
import { GemstoneSize } from 'src/app/interfaces/gemstone-size';
import { OpenReplayService } from 'src/app/services/open-replay/open-replay.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';

@Injectable({
  providedIn: 'root'
})
export class SelectedAttributeService {
  private openReplayService = inject(OpenReplayService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private arrangementsService = inject(StoneArrangementsService);
  private settingsService = inject(StoneSettingsService);
  private typesService = inject(StoneTypesService);
  private shapesService = inject(StoneShapesService);
  private arrangementsWithSettingsService = inject(StoneArrangementsWithSettingsService);
  private typesWithShapesService = inject(StoneTypesWithShapesService);
  private stoneSizesService = inject(StoneSizesService);
  private skuMapService = inject(SkuMapService);
  private designsService = inject(DesignsService);
  private materialsService = inject(MaterialsService);
  private widthsService = inject(WidthsService);
  private formsService = inject(FormsService);
  private configuredItemService = inject(ConfiguredItemService);
  private checkoutService = inject(CheckoutService);
  private rulesService = inject(RulesService);
  private sizesService = inject(SizesService)
  private loadingService = inject(LoadingService);
  private navigationService = inject(Navigation2Service);
  readonly defaultCustomSKU = '008-EUCF-060-14Y-080';
  undoRedoChange = new Subject();
  configuredItem: WritableSignal<ConfiguredItem> = signal({
    design: null,
    form: null,
    primaryMaterial: null,
    secondaryMaterial: null,
    width: null,
    engravingType: null,
    engravingText: null,
    size: null,
    gemstoneType: null,
    gemstoneArrangement: null,
    gemstoneSize: null,
    gemstoneSetting: null,
    gemstoneShape: null
  });

  private actualConfiguredItemPreviousValue: ConfiguredItem = {
    design: null,
    form: null,
    primaryMaterial: null,
    secondaryMaterial: null,
    width: null,
    engravingType: null,
    engravingText: null,
    size: null,
    gemstoneType: null,
    gemstoneArrangement: null,
    gemstoneSize: null,
    gemstoneSetting: null,
    gemstoneShape: null
  };

  public previousConfiguredItem: WritableSignal<ConfiguredItem> = signal({
    design: null,
    form: null,
    primaryMaterial: null,
    secondaryMaterial: null,
    width: null,
    engravingType: null,
    engravingText: null,
    size: null,
    gemstoneType: null,
    gemstoneArrangement: null,
    gemstoneSize: null,
    gemstoneSetting: null,
    gemstoneShape: null
  });

  public poNumber: WritableSignal<string | null> = signal(null);
  pricingObject = signal<PricingObject | null>(null);
  availableAttributes = computed(async () => {
    this.loadingService.load('selected-attribute:availableAttributes');
    const vlaidAttributes = await this.rulesService.getValidAttributes(this.configuredItem(), untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    this.loadingService.done('selected-attribute:availableAttributes');
    return vlaidAttributes;
  });
  private conflictMap: Map<string, boolean> = new Map();
  conflicts = new BehaviorSubject<string[]>([]);
  private previousConflicts: string[] = [];
  didUserSelectSecondaryMaterial: boolean = false;
  shouldShowUnavailable: WritableSignal<boolean> = signal(false);
  configuredItemKeyOrder: string[] = [];

  undoList = signal<HistoryItem[]>([]);
  redoList = signal<HistoryItem[]>([]);
  systemChanges = signal<SystemChange<any>[]>([]);

  constructor() {
    toObservable(this.configuredItem).pipe(pairwise(), map(([oldValue]) => oldValue)).subscribe(oldConfiguredItem => this.actualConfiguredItemPreviousValue = oldConfiguredItem);
    if (typeof(sessionStorage) !== 'undefined') {
      effect(() => {
        const undoList = this.undoList();
        if (this.checkoutService.b2bData.sessionKey) {
          sessionStorage.setItem(`undo[${this.checkoutService.b2bData.sessionKey}]`, JSON.stringify(undoList));
        }
      });

      effect(() => {
        const redoList = this.redoList();
        if (this.checkoutService.b2bData.sessionKey) {
          sessionStorage.setItem(`redo[${this.checkoutService.b2bData.sessionKey}]`, JSON.stringify(redoList));
        }
      });
    }

    (window as any).selectedAttributeService = this;
  }

  loadUndoRedoLists() {
    this.undoList.set(this.getundoRedoListFromSessionStorage('undo'));
    this.redoList.set(this.getundoRedoListFromSessionStorage('redo'));
  }

  private getundoRedoListFromSessionStorage(key: 'undo' | 'redo') {
    if (typeof(sessionStorage) !== 'undefined' && this.checkoutService.b2bData.sessionKey) {
      const value = sessionStorage.getItem(`${key}[${this.checkoutService.b2bData.sessionKey}]`);
      if (value) {
        return JSON.parse(value);
      }
    }

    return [];
  }

  setConflicts(conflicts: string[]) {
    const upperedConflicts = conflicts.map(c => [`${c.charAt(0).toUpperCase()}${c.substring(1)}`, true]);
    this.conflictMap = new Map(upperedConflicts as any);
    this.conflicts.next(Array.from(this.conflictMap.keys()).filter(k => this.conflictMap.get(k)));
  }

  getValue() {
    return this.configuredItem();
  }

  getAttribute(attr: keyof ConfiguredItem) {
    return this.configuredItem()[attr];
  }

  async mutateDesign(design: Design | null) {
    await this.mutate((c) => c.design = design, 'Design');
  }

  async mutateForm(form: Form | null) {
    await this.mutate((c) => c.form = form, 'Form');
  }

  async mutatePrimaryMaterial(material: Material | null) {
    await this.mutate((c) => c.primaryMaterial = material, 'Edge Material');
  }

  async mutateSecondaryMaterial(material: Material | null) {
    await this.mutate((c) => c.secondaryMaterial = material, 'Center Material');
  }

  async mutateWidth(width: Width | null) {
    await this.mutate((c) => c.width = width, 'Width');
  }

  mutateEngravingType(engravingType: EngravingType | null) {
    this.mutate((c) => c.engravingType = engravingType, 'engravingType');
  }

  mutateEngravingText(engravingText: string | null) {
    this.mutate((c) => c.engravingText = engravingText, 'engravingText');
  }

  async mutateSize(size: Size | null) {
    await this.mutate((c) => c.size = size, 'Size');
  }

  async mutate(mutatorFn: ((value: ConfiguredItem) => void), type: string | null, shouldAPIIframeAlertHost: boolean = true, shouldClearSystemChanges = true) {
    this.rulesService.invalidateReachableCache();
    if (type !== 'engravingType' && type !== 'engravingText') {
      this.undoList.update(undoList => {
        const newList = undoList.slice();
        newList.push({type: this.navigationService.activeView().title, configuredItem: this.configuredItem(), firstSelectedHaloStyleNumber: this.checkoutService.b2bData.selectedHaloStyleNumber ?? null });
        return newList;
      });
      this.redoList.set([]);
    }
    const newConfiguredItem = Object.assign({}, this.configuredItem());
    const previousConfiguredItem = Object.assign({}, this.configuredItem());
    mutatorFn(newConfiguredItem);
    this.configuredItem.set(newConfiguredItem);

    if (shouldAPIIframeAlertHost) {
      const haloStylePromise = (!type || !['engravingText', 'engravingType'].includes(type)) ? this.checkoutService.getSimilarHaloSKUToSelected(this.configuredItem()) : Promise.resolve(this.checkoutService.b2bData.haloStyle);
      // this.updatesForURLChange.next(this.configuredItem());
      // const newURL = this.buildURLFromSelections();
      this.redoList.set([]);

      const size = this.configuredItem().size;
      assert(size, 'Size was null');

      // Price immediately on the best-known style instead of awaiting the
      // halo-SKU reverse lookup first; corrected below if it resolves differently.
      const shouldPrice = !type || !['engravingText'].includes(type) || (!!previousConfiguredItem.engravingText !== !!newConfiguredItem.engravingText);
      const bestKnownStyle = this.checkoutService.b2bData.haloStyle ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.configuredItem());
      if (shouldPrice) {
        this.pricingObject.set({ style: bestKnownStyle, size });
      }

      const haloStyle = await haloStylePromise;
      this.checkoutService.updateB2BData((b2bData) => {
        const engravingFont = this.configuredItem().engravingType?.displayName;
        b2bData.eng = engravingFont && this.configuredItem().engravingText ? this.configuredItem().engravingText : null;
        b2bData.engFont = engravingFont && this.configuredItem().engravingText ? engravingFont : null;
        b2bData.customizerSKU = this.configuredItemService.getNewSKUFromSelectedAttributes(this.configuredItem());
        b2bData.haloStyle = haloStyle;
      });

      const correctedStyle = this.checkoutService.b2bData.haloStyle ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.configuredItem());
      if (shouldPrice && correctedStyle !== bestKnownStyle) {
        this.pricingObject.set({ style: correctedStyle, size });
      }

      if (shouldClearSystemChanges) {
        this.systemChanges.set([]);
      }
      // window.history.replaceState(null, '', newURL);
    }
  }

  save() {
    this.previousConfiguredItem.set(Object.assign({}, this.configuredItem()));
  }
  getConflicts() {
    return this.conflicts.value;
  }

  subscribeToConflictsChange(observerOrNext?: Partial<Observer<string[]>> | ((value: string[]) => void)) {
    this.conflicts.subscribe(observerOrNext);
  }

  setPO(value: string | null) {
    this.poNumber.set(value);
    this.checkoutService.updateB2BData((b2bData) => {
      b2bData.po = value;
    });
  }

  async reset() {
    // const originalSKU = this.checkoutService.originalSKU;
    // assert(originalSKU, 'No sku to reset to');
    const customSKU = this.defaultCustomSKU;
    const size = this.defaultCustomSKU.split('-').slice(-1)[0];
    assert(size, 'Could not get size from default sku');
    const sizeObject = this.sizesService.values().find(s => formatFingerSizeForB2BSKU(s.value) === size);
    const customStyle = this.configuredItemService.getStyleFromCustomSKU(customSKU);
    assert(sizeObject, 'No matching size');
    const haloStyle = await this.skuMapService.getReverse(customStyle, sizeObject.value);
    await this.setConfiguredItemFromSKU(customSKU, (b2bdata) => {
      b2bdata.haloStyle = haloStyle[0]?.halosku;
      b2bdata.selectedHaloStyleNumber = undefined;
    });
    const sizeFromConfiguredItem = this.configuredItem().size;
    assert(sizeFromConfiguredItem, 'Size was null');
    this.pricingObject.set({ style: haloStyle[0]?.halosku ?? customStyle, size: sizeFromConfiguredItem });
    this.undoList.set([]);
    this.redoList.set([]);

    this.systemChanges.set([]);
    // if (this.configuredItemService.isCustomSKU(originalSKU)) {
    //   this.setConfiguredItemFromSKU(originalSKU);
    // } else {
    //   this.loadingService.load('selected-attribute:reset');
    //   await this.setConfiguredItemFromStyle(originalSKU);
    //   this.loadingService.done('selected-attribute:reset');
    // }
    this.setPO(null);
    this.navigationService.navigateTo(0);
  }

  async undo() {
    const lastChange = this.undoList()[this.undoList().length - 1];
    const viewIndexToActivate = this.navigationService.views().findIndex(v => v.title === lastChange.type);
    if (lastChange.type && viewIndexToActivate > -1) {
      if (lastChange.type === 'Gemstones' && !lastChange.configuredItem.gemstoneType) {
        const nonStoneViewToNavigateTo = this.navigationService.views().findIndex(v => v.title === 'Designs');
        this.navigationService.navigateTo(nonStoneViewToNavigateTo === -1 ? 0 : nonStoneViewToNavigateTo);
      } else {
        this.navigationService.navigateTo(viewIndexToActivate);
      }
    }
    this.undoList.update(undoList => undoList.slice(0, -1));
    assert(lastChange, 'Nothing to undo');
    this.redoList.update(redoList => {
      const newList = redoList.slice();
      newList.push({type: lastChange.type, configuredItem: this.configuredItem(), firstSelectedHaloStyleNumber: this.checkoutService.b2bData.selectedHaloStyleNumber ?? null});
      return newList;
    });

    lastChange.configuredItem.engravingText = this.configuredItem().engravingText;
    lastChange.configuredItem.engravingType = this.configuredItem().engravingType;
    this.rulesService.invalidateReachableCache();
    this.configuredItem.set(lastChange.configuredItem);

    const haloStyle = await this.checkoutService.getSimilarHaloSKUToSelected(this.configuredItem(), lastChange.firstSelectedHaloStyleNumber ?? undefined);

    this.checkoutService.updateB2BData((b2bData) => {
      const engravingFont = this.configuredItem().engravingType?.displayName;
      b2bData.eng = engravingFont && this.configuredItem().engravingText ? this.configuredItem().engravingText : null;
      b2bData.engFont = engravingFont && this.configuredItem().engravingText ? engravingFont : null;
      b2bData.customizerSKU = this.configuredItemService.getNewSKUFromSelectedAttributes(this.configuredItem());
      b2bData.haloStyle = haloStyle;
      b2bData.selectedHaloStyleNumber = lastChange.firstSelectedHaloStyleNumber ?? undefined
    });

    const size = this.configuredItem().size;
    assert(size, 'Size was null');
    this.pricingObject.set({ style: haloStyle ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.configuredItem()), size });

    this.undoRedoChange.next(null);

    this.systemChanges.set([]);
  }

  async redo() {
    const lastChange = this.redoList()[this.redoList().length - 1];
    const viewIndexToActivate = this.navigationService.views().findIndex(v => v.title === lastChange.type);
    if (lastChange.type && viewIndexToActivate > -1) {
      this.navigationService.navigateTo(viewIndexToActivate);
    }
    this.redoList.update(redoList => redoList.slice(0, -1));
    assert(lastChange, 'Nothing to redo');
    this.undoList.update(undoList => {
      const newList = undoList.slice();
      newList.push({type: lastChange.type, configuredItem: this.configuredItem(), firstSelectedHaloStyleNumber: this.checkoutService.b2bData.selectedHaloStyleNumber ?? null});
      return newList;
    });

    lastChange.configuredItem.engravingText = this.configuredItem().engravingText;
    lastChange.configuredItem.engravingType = this.configuredItem().engravingType;
    this.rulesService.invalidateReachableCache();
    this.configuredItem.set(lastChange.configuredItem);

    const haloStyle = await this.checkoutService.getSimilarHaloSKUToSelected(this.configuredItem(), lastChange.firstSelectedHaloStyleNumber ?? undefined);

    this.checkoutService.updateB2BData((b2bData) => {
      const engravingFont = this.configuredItem().engravingType?.displayName;
      b2bData.eng = engravingFont && this.configuredItem().engravingText ? this.configuredItem().engravingText : null;
      b2bData.engFont = engravingFont && this.configuredItem().engravingText ? engravingFont : null;
      b2bData.customizerSKU = this.configuredItemService.getNewSKUFromSelectedAttributes(this.configuredItem());
      b2bData.haloStyle = haloStyle;
      b2bData.selectedHaloStyleNumber = lastChange.firstSelectedHaloStyleNumber ?? undefined
    });

    const size = this.configuredItem().size;
    assert(size, 'Size was null');
    this.pricingObject.set({ style: haloStyle ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.configuredItem()), size });

    this.undoRedoChange.next(null);

    this.systemChanges.set([]);
  }

  async setConfiguredItemFromSKU(sku: string, shouldUpdateURLOrPropertyOverridesCallback: boolean | ((b2bData: B2BInput) => void) = true) {
    await this.setConfiguredItemFromSKUWithEngravingAndPO(sku, null, null, null, shouldUpdateURLOrPropertyOverridesCallback);
  }

  async setConfiguredItemFromSKUWithEngravingAndPO(sku: string, engravingText: string | null, engravingType: EngravingType | null, po: string | null, shouldUpdateURLOrPropertyOverridesCallback: boolean | ((b2bData: B2BInput) => void) = true) {
    const attributes = this.configuredItemService.getAttributesFromNewSKU(sku);
    attributes.engravingText = !engravingText ? null : decodeURIComponent(engravingText);
    attributes.engravingType = engravingType;

    const isSKUValid = await this.rulesService.getIsValid(attributes, untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    assertTrue(isSKUValid, 'Invalid SKU');

    this.rulesService.invalidateReachableCache();
    this.configuredItem.set(attributes);
    this.poNumber.set(po);
    this.systemChanges.set([]);

    if (shouldUpdateURLOrPropertyOverridesCallback) {
      this.checkoutService.updateB2BData((b2bData) => {
        const engravingFont = this.configuredItem().engravingType?.displayName;
        b2bData.eng = engravingFont && this.configuredItem().engravingText ? this.configuredItem().engravingText : null;
        b2bData.engFont = engravingFont && this.configuredItem().engravingText ? engravingFont : null;
        b2bData.customizerSKU = sku;
        if (typeof shouldUpdateURLOrPropertyOverridesCallback === 'function') {
          shouldUpdateURLOrPropertyOverridesCallback(b2bData);
        }
      });
      // const newURL = this.buildURLFromSelections();
      // window.history.replaceState(null, '', newURL);
    }
  }


  async setConfiguredItemFromStyle(style: string, fingerSize?: number, isQuarterSize?: boolean, shouldUpdateURLOrPropertyOverridesCallback: boolean | ((b2bData: B2BInput) => void) = true) {
    await this.setConfiguredItemFromStyleWithEngravingAndPO(style, null, null, null, fingerSize, isQuarterSize, shouldUpdateURLOrPropertyOverridesCallback);
  }

  async setConfiguredItemFromStyleWithEngravingAndPO(style: string, engravingText: string | null, engravingType: EngravingType | null, po: string | null, fingerSize?: number, isQuarterSize?: boolean, shouldUpdateURLOrPropertyOverridesCallback: boolean | ((b2bData: B2BInput) => void) = true) {
    this.loadingService.load('selected-attribute:set-item-from-style');
    const attributes = this.configuredItemService.getAttributesFromNewStyleNumber(style);
    attributes.engravingText = !engravingText ? null : decodeURIComponent(engravingText);
    attributes.engravingType = engravingType;

    const availableSizes = await this.rulesService.getSizes(attributes, untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    if (!fingerSize) {
      const mappedAvailableSizes = availableSizes.map(availableSize => this.sizesService.values().find(size => size.id === availableSize));
      mappedAvailableSizes.sort((a, b) => {
        const aValue = a?.value ?? 0;
        const bValue = b?.value ?? 0;
        const diff = Math.abs(8 - (aValue)) - Math.abs(8 - (bValue));
        if (diff === 0) {
          return bValue - aValue;
        }
        return diff;
      });
      const firstAvailableSize = mappedAvailableSizes[0];
      assert(firstAvailableSize, 'No matching size found');
      attributes.size = firstAvailableSize;
    } else {
      const decimalSize = fingerSize;
      const finalSize = isQuarterSize ? decimalSize + 0.25 : decimalSize;
      const sizeObject = this.sizesService.values().find(s => s.value === finalSize);
      assertTrue(!!sizeObject && availableSizes.includes(sizeObject.id), 'Invalid size');
      attributes.size = sizeObject;
    }

    this.rulesService.invalidateReachableCache();
    this.configuredItem.set(attributes);
    this.poNumber.set(po);
    this.systemChanges.set([]);
    if (shouldUpdateURLOrPropertyOverridesCallback) {
      this.checkoutService.updateB2BData((b2bData) => {
        const engravingFont = this.configuredItem().engravingType?.displayName;
        b2bData.eng = engravingFont && this.configuredItem().engravingText ? this.configuredItem().engravingText : null;
        b2bData.engFont = engravingFont && this.configuredItem().engravingText ? engravingFont : null;
        b2bData.customizerSKU = this.configuredItemService.getNewSKUFromSelectedAttributes(this.configuredItem());
        if (typeof shouldUpdateURLOrPropertyOverridesCallback === 'function') {
          shouldUpdateURLOrPropertyOverridesCallback(b2bData);
        }
      });
    }
    this.loadingService.done('selected-attribute:set-item-from-style');
    // const newURL = this.buildURLFromSelections();
    // window.history.replaceState(null, '', newURL);
  }

  addConfiguredItemToUndoList(type: string | null) {
    this.undoList.update(undoList => {
      const newList = undoList.slice();
      newList.push({type, configuredItem: this.configuredItem(), firstSelectedHaloStyleNumber: this.checkoutService.b2bData.selectedHaloStyleNumber ?? null});
      return newList;
    });
    this.redoList.set([]);
  }

  async setClosestByDesign(newDesign: Design) {
    const newAttributes = await this.rulesService.getClosestValidItemByDesign(this.configuredItem(), newDesign.id, untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    const insideMaterial = this.materialsService.values().find(m => m.id === newAttributes.im);
    const outsideMaterial = this.materialsService.values().find(m => m.id === newAttributes.om);
    const width = this.widthsService.values().find(w => w.id === newAttributes.w);
    const form = this.formsService.values().find(f => f.id === newAttributes.f);
    const arrangementWithSetting = this.arrangementsWithSettingsService.values().find(aws => aws.id === newAttributes.sa);
    const typeWithShape = this.typesWithShapesService.values().find(tws => tws.id === newAttributes.st);
    const stoneSize = this.stoneSizesService.values().find(ss => ss.id === newAttributes.ss) ?? null;
    const arrangement = this.arrangementsService.values().find(a => a.id === arrangementWithSetting?.stoneArrangementID) ?? null;
    const setting = this.settingsService.values().find(s => s.id === arrangementWithSetting?.stoneSettingID) ?? null;
    const type = this.typesService.values().find(t => t.id === typeWithShape?.stoneTypeID) ?? null;
    const shape = this.shapesService.values().find(s => s.id === typeWithShape?.stoneShapeID) ?? null;
    assert(insideMaterial && outsideMaterial && width && form, 'Could not find a close match.');

    const changes: ConfiguredItem = { design: null, primaryMaterial: insideMaterial, secondaryMaterial: outsideMaterial, width, form, engravingText: null, engravingType: null, size: null, gemstoneType: type, gemstoneArrangement: arrangement, gemstoneSize: stoneSize, gemstoneSetting: setting, gemstoneShape: shape };
    this.triggerConflictResolvedSelectedValueEvent({ design: newDesign });
    this.setSystemChanges(changes);

    await this.mutate(c => {
      c.primaryMaterial = insideMaterial;
      c.secondaryMaterial = outsideMaterial;
      c.width = width;
      c.form = form;
      c.design = newDesign;
      c.gemstoneType = type;
      c.gemstoneShape = shape;
      c.gemstoneArrangement = arrangement;
      c.gemstoneSetting = setting;
      c.gemstoneSize = stoneSize;
    }, 'Design', undefined, false);
  }

  async setClosestBySize(newSize: Size) {
    const newAttributes = await this.rulesService.getClosestValidItemBySize(this.configuredItem(), newSize.id, untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    const insideMaterial = this.materialsService.values().find(m => m.id === newAttributes.im);
    const outsideMaterial = this.materialsService.values().find(m => m.id === newAttributes.om);
    const width = this.widthsService.values().find(w => w.id === newAttributes.w);
    const form = this.formsService.values().find(f => f.id === newAttributes.f);
    const design = this.designsService.values().find(d => d.id === newAttributes.d);
    const arrangementWithSetting = this.arrangementsWithSettingsService.values().find(aws => aws.id === newAttributes.sa);
    const typeWithShape = this.typesWithShapesService.values().find(tws => tws.id === newAttributes.st);
    const stoneSize = this.stoneSizesService.values().find(ss => ss.id === newAttributes.ss) ?? null;
    const arrangement = this.arrangementsService.values().find(a => a.id === arrangementWithSetting?.stoneArrangementID) ?? null;
    const setting = this.settingsService.values().find(s => s.id === arrangementWithSetting?.stoneSettingID) ?? null;
    const type = this.typesService.values().find(t => t.id === typeWithShape?.stoneTypeID) ?? null;
    const shape = this.shapesService.values().find(s => s.id === typeWithShape?.stoneShapeID) ?? null;
    assert(insideMaterial && design && outsideMaterial && width && form, 'Could not find a close match.');

    const changes: ConfiguredItem = { design: design, primaryMaterial: insideMaterial, secondaryMaterial: outsideMaterial, width, form, engravingText: null, engravingType: null, size: null, gemstoneType: type, gemstoneArrangement: arrangement, gemstoneSize: stoneSize, gemstoneSetting: setting, gemstoneShape: shape };
    this.triggerConflictResolvedSelectedValueEvent({ size: newSize });
    this.setSystemChanges(changes);

    await this.mutate(c => {
      c.primaryMaterial = insideMaterial;
      c.secondaryMaterial = outsideMaterial;
      c.width = width;
      c.form = form;
      c.design = design;
      c.size = newSize;
      c.gemstoneType = type;
      c.gemstoneShape = shape;
      c.gemstoneArrangement = arrangement;
      c.gemstoneSetting = setting;
      c.gemstoneSize = stoneSize;
    }, 'Size', undefined, false);
  }

  async setClosestByForm(newForm: Form) {
    const newAttributes = await this.rulesService.getClosestValidItemByForm(this.configuredItem(), newForm.id, untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    const insideMaterial = this.materialsService.values().find(m => m.id === newAttributes.im);
    const outsideMaterial = this.materialsService.values().find(m => m.id === newAttributes.om);
    const width = this.widthsService.values().find(w => w.id === newAttributes.w);
    const design = this.designsService.values().find(d => d.id === newAttributes.d);
    const arrangementWithSetting = this.arrangementsWithSettingsService.values().find(aws => aws.id === newAttributes.sa);
    const typeWithShape = this.typesWithShapesService.values().find(tws => tws.id === newAttributes.st);
    const stoneSize = this.stoneSizesService.values().find(ss => ss.id === newAttributes.ss) ?? null;
    const arrangement = this.arrangementsService.values().find(a => a.id === arrangementWithSetting?.stoneArrangementID) ?? null;
    const setting = this.settingsService.values().find(s => s.id === arrangementWithSetting?.stoneSettingID) ?? null;
    const type = this.typesService.values().find(t => t.id === typeWithShape?.stoneTypeID) ?? null;
    const shape = this.shapesService.values().find(s => s.id === typeWithShape?.stoneShapeID) ?? null;
    assert(insideMaterial && design && outsideMaterial && width, 'Could not find a close match.');

    const changes: ConfiguredItem = { design: design, primaryMaterial: insideMaterial, secondaryMaterial: outsideMaterial, width, form: null, engravingText: null, engravingType: null, size: null, gemstoneType: type, gemstoneArrangement: arrangement, gemstoneSize: stoneSize, gemstoneSetting: setting, gemstoneShape: shape };
    this.triggerConflictResolvedSelectedValueEvent({ form: newForm });
    this.setSystemChanges(changes);

    await this.mutate(c => {
      c.primaryMaterial = insideMaterial;
      c.secondaryMaterial = outsideMaterial;
      c.width = width;
      c.design = design;
      c.form = newForm;
      c.gemstoneType = type;
      c.gemstoneShape = shape;
      c.gemstoneArrangement = arrangement;
      c.gemstoneSetting = setting;
      c.gemstoneSize = stoneSize;
    }, 'Form', undefined, false);
  }

  async setClosestByPrimaryMaterial(newMaterial: Material) {
    const newAttributes = await this.rulesService.getClosestValidItemByPrimaryMaterial(this.configuredItem(), newMaterial.id, untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    const form = this.formsService.values().find(f => f.id === newAttributes.f);
    const outsideMaterial = this.materialsService.values().find(m => m.id === newAttributes.om);
    const width = this.widthsService.values().find(w => w.id === newAttributes.w);
    const design = this.designsService.values().find(d => d.id === newAttributes.d);
    const arrangementWithSetting = this.arrangementsWithSettingsService.values().find(aws => aws.id === newAttributes.sa);
    const typeWithShape = this.typesWithShapesService.values().find(tws => tws.id === newAttributes.st);
    const stoneSize = this.stoneSizesService.values().find(ss => ss.id === newAttributes.ss) ?? null;
    const arrangement = this.arrangementsService.values().find(a => a.id === arrangementWithSetting?.stoneArrangementID) ?? null;
    const setting = this.settingsService.values().find(s => s.id === arrangementWithSetting?.stoneSettingID) ?? null;
    const type = this.typesService.values().find(t => t.id === typeWithShape?.stoneTypeID) ?? null;
    const shape = this.shapesService.values().find(s => s.id === typeWithShape?.stoneShapeID) ?? null;
    assert(design && outsideMaterial && width && form, 'Could not find a close match.');

    const changes: ConfiguredItem = { design: design, primaryMaterial: null, secondaryMaterial: outsideMaterial, width, form, engravingText: null, engravingType: null, size: null, gemstoneType: type, gemstoneArrangement: arrangement, gemstoneSize: stoneSize, gemstoneSetting: setting, gemstoneShape: shape };
    this.triggerConflictResolvedSelectedValueEvent({ primaryMaterial: newMaterial });
    this.setSystemChanges(changes);

    await this.mutate(c => {
      c.form = form;
      c.secondaryMaterial = outsideMaterial;
      c.width = width;
      c.design = design;
      c.primaryMaterial = newMaterial;
      c.gemstoneType = type;
      c.gemstoneShape = shape;
      c.gemstoneArrangement = arrangement;
      c.gemstoneSetting = setting;
      c.gemstoneSize = stoneSize;
    }, 'Edge Material', undefined, false);
  }

  async setClosestBySecondaryMaterial(newMaterial: Material) {
    const newAttributes = await this.rulesService.getClosestValidItemBySecondaryMaterial(this.configuredItem(), newMaterial.id, untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    const form = this.formsService.values().find(f => f.id === newAttributes.f);
    const insideMaterial = this.materialsService.values().find(m => m.id === newAttributes.im);
    const width = this.widthsService.values().find(w => w.id === newAttributes.w);
    const design = this.designsService.values().find(d => d.id === newAttributes.d);
    const arrangementWithSetting = this.arrangementsWithSettingsService.values().find(aws => aws.id === newAttributes.sa);
    const typeWithShape = this.typesWithShapesService.values().find(tws => tws.id === newAttributes.st);
    const stoneSize = this.stoneSizesService.values().find(ss => ss.id === newAttributes.ss) ?? null;
    const arrangement = this.arrangementsService.values().find(a => a.id === arrangementWithSetting?.stoneArrangementID) ?? null;
    const setting = this.settingsService.values().find(s => s.id === arrangementWithSetting?.stoneSettingID) ?? null;
    const type = this.typesService.values().find(t => t.id === typeWithShape?.stoneTypeID) ?? null;
    const shape = this.shapesService.values().find(s => s.id === typeWithShape?.stoneShapeID) ?? null;
    assert(insideMaterial && design && width && form, 'Could not find a close match.');

    const changes: ConfiguredItem = { design: design, primaryMaterial: insideMaterial, secondaryMaterial: null, width, form, engravingText: null, engravingType: null, size: null, gemstoneType: type, gemstoneArrangement: arrangement, gemstoneSize: stoneSize, gemstoneSetting: setting, gemstoneShape: shape };
    this.triggerConflictResolvedSelectedValueEvent({ secondaryMaterial: newMaterial });
    this.setSystemChanges(changes);

    await this.mutate(c => {
      c.form = form;
      c.primaryMaterial = insideMaterial;
      c.width = width;
      c.design = design;
      c.secondaryMaterial = newMaterial;
      c.gemstoneType = type;
      c.gemstoneShape = shape;
      c.gemstoneArrangement = arrangement;
      c.gemstoneSetting = setting;
      c.gemstoneSize = stoneSize;
    }, 'Outside Material', undefined, false);
  }

  async setClosestByWidth(newWidth: Width) {
    const newAttributes = await this.rulesService.getClosestValidItemByWidth(this.configuredItem(), newWidth.id, untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    const insideMaterial = this.materialsService.values().find(m => m.id === newAttributes.im);
    const outsideMaterial = this.materialsService.values().find(m => m.id === newAttributes.om);
    const form = this.formsService.values().find(f => f.id === newAttributes.f);
    const design = this.designsService.values().find(d => d.id === newAttributes.d);
    const arrangementWithSetting = this.arrangementsWithSettingsService.values().find(aws => aws.id === newAttributes.sa);
    const typeWithShape = this.typesWithShapesService.values().find(tws => tws.id === newAttributes.st);
    const stoneSize = this.stoneSizesService.values().find(ss => ss.id === newAttributes.ss) ?? null;
    const arrangement = this.arrangementsService.values().find(a => a.id === arrangementWithSetting?.stoneArrangementID) ?? null;
    const setting = this.settingsService.values().find(s => s.id === arrangementWithSetting?.stoneSettingID) ?? null;
    const type = this.typesService.values().find(t => t.id === typeWithShape?.stoneTypeID) ?? null;
    const shape = this.shapesService.values().find(s => s.id === typeWithShape?.stoneShapeID) ?? null;
    assert(insideMaterial && design && outsideMaterial && form, 'Could not find a close match.');

    const changes: ConfiguredItem = { design: design, primaryMaterial: insideMaterial, secondaryMaterial: outsideMaterial, width: null, form, engravingText: null, engravingType: null, size: null, gemstoneType: type, gemstoneArrangement: arrangement, gemstoneSize: stoneSize, gemstoneSetting: setting, gemstoneShape: shape };
    this.triggerConflictResolvedSelectedValueEvent({ width: newWidth });
    this.setSystemChanges(changes);

    await this.mutate(c => {
      c.primaryMaterial = insideMaterial;
      c.secondaryMaterial = outsideMaterial;
      c.form = form;
      c.design = design;
      c.width = newWidth;
      c.gemstoneType = type;
      c.gemstoneShape = shape;
      c.gemstoneArrangement = arrangement;
      c.gemstoneSetting = setting;
      c.gemstoneSize = stoneSize;
    }, 'Width', undefined, false);
  }

  async setClosestByStoneTypeWithShape(newTypeWithShape: GemstoneTypeWithShape, configuredItemOverride?: ConfiguredItem, shouldIgnoreStoneChanges=false) {
    const configuredItemToUse = configuredItemOverride ?? this.configuredItem();
    const newAttributes = await this.rulesService.getClosestValidItemByStoneTypeWithShape(configuredItemToUse, newTypeWithShape.id, untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    const width = this.widthsService.values().find(w => w.id === newAttributes.w);
    const insideMaterial = this.materialsService.values().find(m => m.id === newAttributes.im);
    const outsideMaterial = this.materialsService.values().find(m => m.id === newAttributes.om);
    const form = this.formsService.values().find(f => f.id === newAttributes.f);
    const design = this.designsService.values().find(d => d.id === newAttributes.d);
    const arrangementWithSetting = this.arrangementsWithSettingsService.values().find(aws => aws.id === newAttributes.sa);
    const stoneSize = this.stoneSizesService.values().find(ss => ss.id === newAttributes.ss);
    const arrangement = this.arrangementsService.values().find(a => a.id === arrangementWithSetting?.stoneArrangementID);
    const setting = this.settingsService.values().find(s => s.id === arrangementWithSetting?.stoneSettingID);
    const type = this.typesService.values().find(t => t.id === newTypeWithShape.stoneTypeID);
    const shape = this.shapesService.values().find(s => s.id === newTypeWithShape.stoneShapeID);
    assert(width && insideMaterial && design && outsideMaterial && form && arrangementWithSetting && stoneSize && arrangement && setting && type && shape, 'Could not find a close match.');

    const changes: ConfiguredItem = { design: design, primaryMaterial: insideMaterial, secondaryMaterial: outsideMaterial, width: width, form, engravingText: null, engravingType: null, size: null, gemstoneType: null, gemstoneArrangement: arrangement, gemstoneSize: stoneSize, gemstoneSetting: setting, gemstoneShape: null };
    if (shouldIgnoreStoneChanges) {
      changes.gemstoneArrangement = null;
      changes.gemstoneSize = null;
      changes.gemstoneSetting = null;
    }

    this.triggerConflictResolvedSelectedValueEvent({ gemstoneType: type, gemstoneShape: shape });
    this.setSystemChanges(changes);

    await this.mutate(c => {
      c.primaryMaterial = insideMaterial;
      c.secondaryMaterial = outsideMaterial;
      c.form = form;
      c.design = design;
      c.width = width;
      c.gemstoneType = type;
      c.gemstoneShape = shape;
      c.gemstoneArrangement = arrangement;
      c.gemstoneSetting = setting;
      c.gemstoneSize = stoneSize;
    }, 'Gemstone', undefined, false);
  }

  async setClosestByStoneArrangementWithSetting(newArrangementWithSetting: GemstoneArrangementWithSetting, shouldIgnoreStoneChanges=false) {
    const newAttributes = await this.rulesService.getClosestValidItemByStoneArrangementWithSetting(this.configuredItem(), newArrangementWithSetting.id, untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    const width = this.widthsService.values().find(w => w.id === newAttributes.w);
    const insideMaterial = this.materialsService.values().find(m => m.id === newAttributes.im);
    const outsideMaterial = this.materialsService.values().find(m => m.id === newAttributes.om);
    const form = this.formsService.values().find(f => f.id === newAttributes.f);
    const design = this.designsService.values().find(d => d.id === newAttributes.d);
    const typeWithShape = this.typesWithShapesService.values().find(tws => tws.id === newAttributes.st);
    const stoneSize = this.stoneSizesService.values().find(ss => ss.id === newAttributes.ss);
    const arrangement = this.arrangementsService.values().find(a => a.id === newArrangementWithSetting.stoneArrangementID);
    const setting = this.settingsService.values().find(s => s.id === newArrangementWithSetting.stoneSettingID);
    const type = this.typesService.values().find(t => t.id === typeWithShape?.stoneTypeID);
    const shape = this.shapesService.values().find(s => s.id === typeWithShape?.stoneShapeID);
    assert(width && insideMaterial && design && outsideMaterial && form && typeWithShape && stoneSize && arrangement && setting && type && shape, 'Could not find a close match.');

    const changes: ConfiguredItem = { design: design, primaryMaterial: insideMaterial, secondaryMaterial: outsideMaterial, width: width, form, engravingText: null, engravingType: null, size: null, gemstoneType: type, gemstoneArrangement: null, gemstoneSize: stoneSize, gemstoneSetting: null, gemstoneShape: shape };
    if (shouldIgnoreStoneChanges) {
      changes.gemstoneArrangement = null;
      changes.gemstoneSize = null;
      changes.gemstoneSetting = null;
    }

    this.triggerConflictResolvedSelectedValueEvent({ gemstoneArrangement: arrangement, gemstoneSetting: setting });
    this.setSystemChanges(changes);

    await this.mutate(c => {
      c.primaryMaterial = insideMaterial;
      c.secondaryMaterial = outsideMaterial;
      c.form = form;
      c.design = design;
      c.width = width;
      c.gemstoneType = type;
      c.gemstoneShape = shape;
      c.gemstoneArrangement = arrangement;
      c.gemstoneSetting = setting;
      c.gemstoneSize = stoneSize;
    }, 'Gemstone', undefined, false);
  }

  async setClosestByStoneSize(newStoneSize: GemstoneSize, shouldIgnoreStoneChanges=false) {
    const newAttributes = await this.rulesService.getClosestValidItemByStoneSize(this.configuredItem(), newStoneSize.id, untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
    const width = this.widthsService.values().find(w => w.id === newAttributes.w);
    const insideMaterial = this.materialsService.values().find(m => m.id === newAttributes.im);
    const outsideMaterial = this.materialsService.values().find(m => m.id === newAttributes.om);
    const form = this.formsService.values().find(f => f.id === newAttributes.f);
    const design = this.designsService.values().find(d => d.id === newAttributes.d);
    const arrangementWithSetting = this.arrangementsWithSettingsService.values().find(aws => aws.id === newAttributes.sa);
    const typeWithShape = this.typesWithShapesService.values().find(tws => tws.id === newAttributes.st);
    const stoneSize = this.stoneSizesService.values().find(ss => ss.id === newAttributes.ss);
    const arrangement = this.arrangementsService.values().find(a => a.id === arrangementWithSetting?.stoneArrangementID);
    const setting = this.settingsService.values().find(s => s.id === arrangementWithSetting?.stoneSettingID);
    const type = this.typesService.values().find(t => t.id === typeWithShape?.stoneTypeID);
    const shape = this.shapesService.values().find(s => s.id === typeWithShape?.stoneShapeID);
    assert(width && insideMaterial && design && outsideMaterial && form && arrangementWithSetting && typeWithShape && stoneSize && arrangement && setting && type && shape, 'Could not find a close match.');

    const changes: ConfiguredItem = { design: design, primaryMaterial: insideMaterial, secondaryMaterial: outsideMaterial, width: width, form, engravingText: null, engravingType: null, size: null, gemstoneType: type, gemstoneArrangement: arrangement, gemstoneSize: null, gemstoneSetting: setting, gemstoneShape: shape };
    if (shouldIgnoreStoneChanges) {
      changes.gemstoneArrangement = null;
      changes.gemstoneSize = null;
      changes.gemstoneSetting = null;
    }

    this.triggerConflictResolvedSelectedValueEvent({ gemstoneSize: newStoneSize });
    this.setSystemChanges(changes);

    await this.mutate(c => {
      c.primaryMaterial = insideMaterial;
      c.secondaryMaterial = outsideMaterial;
      c.form = form;
      c.design = design;
      c.width = width;
      c.gemstoneType = type;
      c.gemstoneShape = shape;
      c.gemstoneArrangement = arrangement;
      c.gemstoneSetting = setting;
      c.gemstoneSize = stoneSize;
    }, 'Gemstone', undefined, false);
  }

  private triggerConflictResolvedSelectedValueEvent(selectedValueOverride: Partial<ConfiguredItem>) {
    try {
      const oldConfiguration = this.configuredItemService.getNewSKUFromSelectedAttributes(this.configuredItem());
      const selectedValueConfiguration = this.configuredItemService.getNewSKUFromSelectedAttributes(Object.assign({}, this.configuredItem(), selectedValueOverride));
      if (this.googleAnalyticsService.isInitialized) {
        this.googleAnalyticsService.triggerEvent('conflict_resolved_selected_value', 'attribute_panel', 'conflict_resolved_selected_value', `${oldConfiguration}:${selectedValueConfiguration}`);
      }
    } catch (e) {
      console.error('Failed to record event');
    }
  }

  setSystemChanges(newChanges: ConfiguredItem) {
    const systemChanges: SystemChange<any>[] = (Object.keys(newChanges) as (keyof ConfiguredItem)[]).filter((key) => (newChanges[key] || key === 'gemstoneType') && ((this.configuredItem()[key] as any)?.id ?? this.configuredItem()[key]) !== ((newChanges[key] as any)?.id ?? newChanges[key])).map((key) => ({
      key,
      oldValue: this.configuredItem()[key],
      newValue: newChanges[key]
    }));
    this.systemChanges.set(systemChanges);

    try {
      const oldConfiguration = this.configuredItemService.getNewSKUFromSelectedAttributes(this.configuredItem());
      const newChangesForNewConfiguration = Object.assign({}, newChanges);
      (Object.keys(newChangesForNewConfiguration) as (keyof ConfiguredItem)[]).forEach((key) => {
        if (!newChangesForNewConfiguration[key]) {
          delete newChangesForNewConfiguration[key];
        }
      });
      const newConfiguration = this.configuredItemService.getNewSKUFromSelectedAttributes(Object.assign({}, this.configuredItem(), newChangesForNewConfiguration));
      this.openReplayService.sendEvent('Conflict Resolved', { oldConfiguration, newConfiguration });
      if (this.googleAnalyticsService.isInitialized) {
        this.googleAnalyticsService.triggerEvent('conflict_resolved', 'attribute_panel', 'conflict_resolved', `${oldConfiguration}:${newConfiguration}`);
      }
    } catch (e) {
      console.error('Failed to record event');
      console.error(e);
    }
  }

  removeGemstones() {
    this.mutate(c => {
      c.gemstoneArrangement = null;
      c.gemstoneSetting = null;
      c.gemstoneShape = null;
      c.gemstoneType = null;
      c.gemstoneSize = null;
      return c;
    }, 'Gemstone');
  }
}
